import type { AccountOptionDto, CategoryOptionDto, DraftInput, ParsedDraftDto } from "@home/shared";
import { useEffect, useState } from "react";
import { api } from "../api.ts";
import { dayTitle, money, todayIso } from "../format.ts";
import { t, type Lang } from "../i18n.ts";
import { errorText, useMeta } from "../meta.ts";
import { hapticSelect, hapticTap, userCacheKey } from "../telegram.ts";
import { opsQueue } from "../opsQueue.ts";
import { newId } from "../sync/engine.ts";
import { invalidateRemote } from "../useRemote.ts";
import { defaultAccount, defaultSource } from "../accounts.ts";
import { cleanAmount, groupDigits, parseAmount } from "../amount.ts";
import { AmountField, type Currency } from "./AmountField.tsx";
import { Segmented } from "./Segmented.tsx";
import { PrimaryAction, Sheet } from "./Sheet.tsx";
import { toast } from "./Toast.tsx";

export type TxType = "expense" | "income" | "transfer";

/** Сколько категорий видно сразу; остальные — по кнопке «Все». */
const TOP_CATEGORIES = 8;

/** Новая операция: форма (тип, сумма, категория…) или фраза, которую разберёт ИИ, как в боте. */
export function AddTxSheet({ lang, initialType = "expense", onClose }: { lang: Lang; initialType?: TxType; onClose: () => void }) {
  const [mode, setMode] = useState<"form" | "phrase">("form");
  return (
    <Sheet title={t("newOp")} onClose={onClose}>
      {mode === "form" ? (
        <TxForm lang={lang} initialType={initialType} onDone={onClose} onPhrase={() => setMode("phrase")} />
      ) : (
        <PhraseForm lang={lang} onDone={onClose} onForm={() => setMode("form")} />
      )}
    </Sheet>
  );
}

/** Записано: на сервере — «Сохранено»; без сети — в очереди на телефоне. */
function saved(result: "sent" | "queued" = "sent") {
  hapticTap();
  toast(result === "queued" ? t("savedOffline") : t("savedOk"));
  if (result === "sent") invalidateRemote();
}

/** Последний выбранный счёт для расходов / доходов: в следующий раз выбран он. */
function lastAccountKey(type: TxType, currency: Currency): string {
  return `last-account:${userCacheKey()}:${type}:${currency}`;
}

function readLastAccount(type: TxType, currency: Currency): string | null {
  try {
    return localStorage.getItem(lastAccountKey(type, currency));
  } catch {
    return null;
  }
}

function rememberAccount(type: TxType, currency: Currency, id: string): void {
  try {
    localStorage.setItem(lastAccountKey(type, currency), id);
  } catch {
    // приватный режим — просто не запомним
  }
}

/** Второстепенная сумма (сколько пришло при обмене, комиссия) — обычное поле, не крупное. */
function SmallAmountField(props: { label: string; value: string; onChange: (raw: string) => void; currency: Currency; lang: Lang; hint?: string }) {
  const usd = props.currency === "USD";
  return (
    <div className="field">
      <span>{props.label}</span>
      <div className="small-amount">
        <input
          className="input"
          inputMode={usd ? "decimal" : "numeric"}
          enterKeyHint="done"
          placeholder="0"
          aria-label={props.label}
          value={groupDigits(props.value)}
          onChange={(e) => props.onChange(cleanAmount(e.target.value, usd))}
        />
        <span className="amount-unit">{usd ? "$" : props.lang === "uz" ? "so'm" : "сум"}</span>
      </div>
      {props.hint && <div className="hint">{props.hint}</div>}
    </div>
  );
}

function AccountChips(props: { label: string; accounts: AccountOptionDto[]; value: string | null; onChange: (id: string) => void }) {
  return (
    <div className="field">
      <span>{props.label}</span>
      <div className="chips" role="radiogroup" aria-label={props.label}>
        {props.accounts.map((a) => (
          <button
            key={a.id}
            type="button"
            role="radio"
            aria-checked={a.id === props.value}
            className={a.id === props.value ? "chip active" : "chip"}
            onClick={() => {
              hapticSelect();
              props.onChange(a.id);
            }}
          >
            {a.stash ? "🐷" : a.currency === "USD" ? "💵" : "💳"} {a.name}
          </button>
        ))}
      </div>
    </div>
  );
}

function TxForm({ lang, initialType, onDone, onPhrase }: { lang: Lang; initialType: TxType; onDone: () => void; onPhrase: () => void }) {
  const meta = useMeta();
  const [type, setType] = useState<TxType>(initialType);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState<Currency>("UZS");
  const [category, setCategory] = useState<string | null>(null);
  const [toUserId, setToUserId] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [date, setDate] = useState(todayIso());
  const [personal, setPersonal] = useState(false);
  // Счёт операции (расход — откуда, доход — куда); null — последний выбранный или основной.
  const [accountId, setAccountId] = useState<string | null>(null);
  // Перевод: члену семьи или между своими счетами (откуда → куда, при обмене — сколько пришло, комиссия).
  const [transferKind, setTransferKind] = useState<"member" | "own">("member");
  const [fromId, setFromId] = useState<string | null>(null);
  const [toId, setToId] = useState<string | null>(null);
  const [received, setReceived] = useState("");
  const [fee, setFee] = useState("");
  // Дата, комментарий и «личная» нужны редко — свёрнуты в одну строку с текущими значениями.
  const [details, setDetails] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // Один id на форму: если «Записать» нажали снова после ошибки, сервер не создаст вторую запись.
  const [clientId] = useState(newId);
  const [feeClientId] = useState(newId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setError(null), [amount, category, toUserId, fromId, toId]);

  const categories = (meta.data?.categories ?? []).filter((c) => c.kind === type);
  const recipients = (meta.data?.members ?? []).filter((m) => !m.isMe && m.role !== "child");
  const accounts = meta.data?.accounts ?? [];
  // Переводить между своими счетами можно, когда их хотя бы два; члену семьи — когда есть другие взрослые.
  const canOwn = accounts.length > 1;
  const kind = !canOwn ? "member" : recipients.length === 0 && meta.data ? "own" : transferKind;
  const own = type === "transfer" && kind === "own";
  const from = accounts.find((a) => a.id === fromId) ?? null;
  const to = accounts.find((a) => a.id === toId) ?? null;
  const exchange = own && from !== null && to !== null && from.currency !== to.currency;

  // Счёт расхода, дохода или перевода члену семьи: выбранный, иначе последний, иначе основной.
  const inCurrency = accounts.filter((a) => a.currency === currency);
  const account =
    inCurrency.find((a) => a.id === accountId) ??
    inCurrency.find((a) => a.id === readLastAccount(type, currency) && !a.stash) ??
    // Перевод члену семьи, как и на сервере, по умолчанию — со своего личного счёта.
    defaultAccount(accounts, currency, personal || type === "transfer");

  const changeType = (next: TxType) => {
    setType(next);
    setCategory(null);
    setShowAll(false);
    setAccountId(null);
    setError(null);
  };

  const pickFrom = (id: string) => {
    const a = accounts.find((x) => x.id === id);
    setFromId(id);
    if (a) setCurrency(a.currency);
    // У сумов копеек нет — дробная часть от долларов не нужна.
    if (a?.currency === "UZS") setAmount((v) => v.split(".")[0] ?? "");
    if (toId === id) setToId(null);
    setReceived("");
  };

  const submit = async () => {
    if (busy) return;
    const value = parseAmount(amount);
    const problem = !value
      ? t("fillAmount")
      : own
        ? !from
          ? t("pickFromAccount")
          : !to
            ? t("pickToAccount")
            : null
        : type === "transfer"
          ? toUserId
            ? null
            : t("pickMember")
          : category
            ? null
            : t("pickCategory");
    if (problem || !value) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    const base = { currency, comment: comment.trim() || null, date, items: [] };
    const drafts: DraftInput[] = own
      ? [
          {
            ...base,
            type: "transfer",
            amount: value,
            currency: from!.currency,
            category: null,
            transferToUserId: null,
            scope: "shared",
            clientId,
            accountId: from!.id,
            toAccountId: to!.id,
            // Обмен валюты: сколько пришло; пусто — сервер посчитает по курсу ЦБ.
            toAmount: exchange ? parseAmount(received) : null,
          },
        ]
      : [
          {
            ...base,
            type,
            amount: value,
            category: type === "transfer" ? null : category,
            transferToUserId: type === "transfer" ? toUserId : null,
            scope: type !== "transfer" && personal ? "personal" : "shared",
            clientId,
            // Счёт показан выбранным — его и записываем (один счёт в валюте — решает сервер).
            accountId: inCurrency.length > 1 ? (account?.id ?? null) : null,
          },
        ];
    // Комиссия за перевод — отдельным расходом «Комиссии» с того же счёта.
    const feeValue = type === "transfer" ? parseAmount(fee) : null;
    if (feeValue) {
      drafts.push({
        ...base,
        type: "expense",
        amount: feeValue,
        currency: own ? from!.currency : currency,
        category: "fees",
        transferToUserId: null,
        scope: "shared",
        comment: t("commissionNote"),
        clientId: feeClientId,
        accountId: own ? from!.id : inCurrency.length > 1 ? (account?.id ?? null) : null,
      });
    }
    try {
      saved(await opsQueue().submit(drafts));
      if (!own && account && inCurrency.length > 1) rememberAccount(type, currency, account.id);
      onDone();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <div className="form">
      <Segmented
        value={type}
        onChange={changeType}
        label={t("type")}
        options={[
          { value: "expense", label: t("expense") },
          { value: "income", label: t("income") },
          { value: "transfer", label: `🔁 ${t("transfer")}` },
        ]}
      />
      {type === "transfer" && canOwn && recipients.length > 0 && (
        <Segmented
          value={kind}
          onChange={(k) => {
            setTransferKind(k);
            setError(null);
          }}
          label={t("transfer")}
          options={[
            { value: "member", label: t("transferMember") },
            { value: "own", label: t("transferOwn") },
          ]}
        />
      )}
      {/* Между своими счетами валюта — счёта «откуда». */}
      <AmountField value={amount} onChange={setAmount} currency={currency} onCurrency={own ? undefined : setCurrency} lang={lang} />

      {own ? (
        <>
          <AccountChips label={t("fromAccount")} accounts={accounts} value={fromId} onChange={pickFrom} />
          <AccountChips label={t("toAccount")} accounts={accounts.filter((a) => a.id !== fromId)} value={toId} onChange={setToId} />
          {exchange && (
            <SmallAmountField label={t("received")} value={received} onChange={setReceived} currency={to!.currency} lang={lang} hint={t("receivedHint")} />
          )}
        </>
      ) : type === "transfer" ? (
        <div className="field">
          <span>{t("toWhom")}</span>
          {meta.data && recipients.length === 0 ? (
            <p className="hint">{t("noOtherAdults")}</p>
          ) : (
            <div className="chips" role="radiogroup" aria-label={t("toWhom")}>
              {recipients.map((m) => (
                <button
                  key={m.userId}
                  type="button"
                  role="radio"
                  aria-checked={toUserId === m.userId}
                  className={toUserId === m.userId ? "chip active" : "chip"}
                  onClick={() => {
                    hapticSelect();
                    setToUserId(m.userId);
                  }}
                >
                  👤 {m.name}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <CategoryGrid
          categories={categories}
          loading={!meta.data && meta.loading}
          value={category}
          showAll={showAll}
          onShowAll={() => setShowAll(true)}
          onChange={(code) => {
            hapticSelect();
            setCategory(code);
          }}
        />
      )}

      {/* Счетов в валюте операции несколько — видно, откуда платили или куда пришло. */}
      {!own && inCurrency.length > 1 && (
        <AccountChips label={type === "income" ? t("toAccount") : t("fromAccount")} accounts={inCurrency} value={account?.id ?? null} onChange={setAccountId} />
      )}
      {type === "transfer" && (
        <SmallAmountField label={t("commission")} value={fee} onChange={setFee} currency={own && from ? from.currency : currency} lang={lang} />
      )}

      {details ? (
        <>
          <input className="input" placeholder={t("commentPh")} value={comment} onChange={(e) => setComment(e.target.value)} maxLength={200} />
          <DateChips value={date} onChange={setDate} lang={lang} />
          {type !== "transfer" && (
            <label className="toggle">
              <input type="checkbox" checked={personal} onChange={(e) => setPersonal(e.target.checked)} />
              <span>{t("personalOp")}</span>
            </label>
          )}
        </>
      ) : (
        <button
          type="button"
          className="details-toggle"
          onClick={() => {
            hapticSelect();
            setDetails(true);
          }}
        >
          <span className="row-icon" aria-hidden="true">
            📅
          </span>
          <span className="details-sum">
            <span className="row-title">{t("detailsTitle")}</span>
            <span className="row-meta">{[dayTitle(date, lang), type === "transfer" ? null : personal ? t("personalShort") : t("sharedShort")].filter(Boolean).join(" · ")}</span>
          </span>
          <span className="details-change">{t("change")}</span>
        </button>
      )}

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <button type="button" className="btn phrase-btn" onClick={onPhrase}>
        {t("byPhrase")}
      </button>
      <PrimaryAction label={t("addOp")} onClick={() => void submit()} busy={busy} />
    </div>
  );
}

function CategoryGrid(props: {
  categories: CategoryOptionDto[];
  loading: boolean;
  value: string | null;
  showAll: boolean;
  onShowAll: () => void;
  onChange: (code: string) => void;
}) {
  const { categories, value } = props;
  const collapsed = !props.showAll && categories.length > TOP_CATEGORIES + 1;
  const visible = collapsed ? categories.slice(0, TOP_CATEGORIES) : categories;
  return (
    <div className="field">
      <span>{t("category")}</span>
      {props.loading ? (
        <div className="skeleton skeleton-block" />
      ) : (
        <div className="cat-grid" role="radiogroup" aria-label={t("category")}>
          {visible.map((c) => (
            <button
              key={c.code}
              type="button"
              role="radio"
              aria-checked={c.code === value}
              className={c.code === value ? "cat-tile active" : "cat-tile"}
              onClick={() => props.onChange(c.code)}
            >
              <span className="cat-tile-icon" aria-hidden="true">
                {c.icon}
              </span>
              <span className="cat-tile-name">{c.name}</span>
            </button>
          ))}
          {collapsed && (
            <button type="button" className="cat-tile more" onClick={props.onShowAll}>
              <span className="cat-tile-icon" aria-hidden="true">
                ⋯
              </span>
              <span className="cat-tile-name">{t("allCategories")}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function DateChips({ value, onChange, lang }: { value: string; onChange: (date: string) => void; lang: Lang }) {
  const today = todayIso();
  const yesterday = todayIso(-1);
  const other = value !== today && value !== yesterday;
  const pick = (date: string) => {
    hapticSelect();
    onChange(date);
  };
  return (
    <div className="chips" role="radiogroup" aria-label={t("date")}>
      <button type="button" role="radio" aria-checked={value === today} className={value === today ? "chip active" : "chip"} onClick={() => pick(today)}>
        {t("today")}
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={value === yesterday}
        className={value === yesterday ? "chip active" : "chip"}
        onClick={() => pick(yesterday)}
      >
        {t("yesterday")}
      </button>
      {/* Невидимое поле даты поверх чипа: нажатие открывает системный календарь. */}
      <label className={other ? "chip date-chip active" : "chip date-chip"}>
        📅 {other ? dayTitle(value, lang) : t("otherDate")}
        <input type="date" value={value} max={today} aria-label={t("date")} onChange={(e) => e.target.value && pick(e.target.value)} />
      </label>
    </div>
  );
}

function toDraft(d: ParsedDraftDto & { clientId?: string }): DraftInput {
  const own = d.type === "transfer" && d.toAccountId !== null;
  return {
    clientId: d.clientId,
    type: d.type,
    amount: d.amount ?? 0,
    currency: d.currency,
    category: d.type === "transfer" ? null : d.category,
    transferToUserId: d.type === "transfer" && !own ? d.transferToUserId : null,
    scope: d.scope,
    comment: d.comment,
    date: d.date,
    items: d.items,
    // Счёт из фразы («зарплата UZKEK», «перевёл с карты на наличку»); не назван — основной.
    accountId: d.accountId,
    toAccountId: own ? d.toAccountId : null,
    toAmount: own ? d.toAmount : null,
  };
}

/** Фраза как в боте: «такси 25 тысяч и кофе 18 000» → черновики → проверить и сохранить. */
function PhraseForm({ lang, onDone, onForm }: { lang: Lang; onDone: () => void; onForm: () => void }) {
  const meta = useMeta();
  const [text, setText] = useState("");
  const [drafts, setDrafts] = useState<(ParsedDraftDto & { clientId?: string })[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<"parse" | "save" | null>(null);
  const ready = drafts !== null && drafts.length > 0;

  const recognize = async () => {
    if (busy) return;
    if (!text.trim()) {
      setNote(t("notRecognized"));
      return;
    }
    setBusy("parse");
    setNote(null);
    setDrafts(null);
    try {
      const res = await api.parse(text.trim());
      const complete = res.drafts.length > 0 && res.drafts.every((d) => d.amount !== null && d.amount > 0);
      if (res.status === "debt") setNote(t("phraseDebt"));
      // Id записей — при разборе: повторное «Сохранить» после ошибки не создаст дублей.
      else if (res.status === "ok" && complete) setDrafts(res.drafts.map((d) => ({ ...d, clientId: newId() })));
      else setNote(res.clarification ?? t("notRecognized"));
    } catch (err) {
      setNote(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const saveAll = async () => {
    if (busy || !drafts) return;
    setBusy("save");
    setNote(null);
    try {
      saved(await opsQueue().submit(drafts.map(toDraft), "text"));
      onDone();
    } catch (err) {
      setNote(errorText(err));
      setBusy(null);
    }
  };

  const categoryOf = (code: string | null) => meta.data?.categories.find((c) => c.code === code);

  return (
    <div className="form">
      <textarea
        className="input"
        rows={3}
        autoFocus
        placeholder={t("phrasePh")}
        value={text}
        maxLength={1000}
        onChange={(e) => {
          setText(e.target.value);
          setDrafts(null);
        }}
      />
      <p className="hint">{t("phraseHint")}</p>
      {busy === "parse" && <p className="hint">⏳ {t("recognizing")}</p>}
      {note && (
        <p className="form-error" role="alert">
          {note}
        </p>
      )}

      {ready && (
        <ul className="draft-list">
          {drafts.map((d, i) => {
            const cat = categoryOf(d.category);
            const sign = d.type === "income" ? 1 : d.type === "expense" ? -1 : 0;
            // Между своими счетами: «UZKEK → Farmi», при обмене — сколько пришло.
            const own = d.type === "transfer" && d.toAccountName !== null;
            const accounts = meta.data?.accounts ?? [];
            const from =
              d.accountName ??
              (own && d.toAccountId
                ? defaultSource(accounts, d.currency, d.toAccountId)
                : defaultAccount(accounts, d.currency, d.type === "transfer" || d.scope === "personal")
              )?.name ??
              "";
            const toCurrency = d.currency === "USD" ? "UZS" : "USD";
            const accountLine = own
              ? `${from} → ${d.toAccountName}${d.toAmount ? ` · ${t("receivedL")} ${money(d.toAmount, toCurrency, lang)}` : ""}`
              : accounts.length > 1 && from
                ? `💳 ${from}`
                : null;
            return (
              <li key={i} className="row">
                <span className="row-icon" aria-hidden="true">
                  {d.type === "transfer" ? "🔁" : (cat?.icon ?? "💸")}
                </span>
                <div className="row-body">
                  <div className="row-title">{d.comment ?? (d.type === "transfer" ? `→ ${d.transferToName ?? d.toAccountName ?? ""}` : (cat?.name ?? ""))}</div>
                  <div className="row-meta">
                    {dayTitle(d.date, lang)}
                    {cat && d.comment ? ` · ${cat.name}` : ""}
                    {d.scope === "personal" ? " · 🔒" : ""}
                    {accountLine ? ` · ${accountLine}` : ""}
                  </div>
                </div>
                <div className={sign > 0 ? "row-end amount income" : "row-end amount"}>
                  {money(sign === 0 ? (d.amount ?? 0) : sign * (d.amount ?? 0), d.currency, lang, sign > 0)}
                </div>
                <button type="button" className="row-remove" aria-label={t("delete")} onClick={() => setDrafts(drafts.filter((_, j) => j !== i))}>
                  ✕
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <button type="button" className="btn-link" onClick={onForm}>
        {t("byForm")}
      </button>
      <PrimaryAction
        label={ready ? t("saveAll", { n: drafts.length }) : t("recognize")}
        onClick={() => void (ready ? saveAll() : recognize())}
        busy={busy !== null}
      />
    </div>
  );
}
